/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AdminDatePickerInputModal,
  AdminRadioInput,
  Button,
  DayOfWeek,
  Div,
  ErrorToast,
  H5,
  Modal,
  PropsModal,
  Separator,
  WorkingHoursReponseV2,
  daysOfWeekString,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  format24hToDate,
  formatDateToTime24h,
  dateTimeInitial,
} from '../../helpers/time-helpers';

export const AddWorkingHours = ({
  modalIsOpen,
  closeModal,
  setWorkingHours,
}: PropsModal & {
  modalIsOpen: {
    edit:
    | null
    | WorkingHoursReponseV2['business'][number]
    | WorkingHoursReponseV2['delivery'][number];
    type: 'business' | 'delivery';
    index: number;
    multiple: boolean;
  } | null;
  setWorkingHours: Dispatch<SetStateAction<WorkingHoursReponseV2>>;
}) => {
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<DayOfWeek[]>([]);
  const [timeFrom, setTimeFrom] = useState<Date[]>([dateTimeInitial]);
  const [timeTo, setTimeTo] = useState<Date[]>([dateTimeInitial]);

  const workingHourData = modalIsOpen;

  useEffect(() => {
    if (workingHourData?.edit) {
      setSelectedDayOfWeek([workingHourData.edit.day]);
      setTimeFrom([format24hToDate(workingHourData.edit.startTime)]);
      setTimeTo([format24hToDate(workingHourData.edit.endTime)]);
    } else {
      setSelectedDayOfWeek([]);
      setTimeFrom([dateTimeInitial]);
      setTimeTo([dateTimeInitial]);
    }
  }, [workingHourData]);

  async function handleAdd() {
    if (selectedDayOfWeek.length === 0 || !timeFrom || !timeTo)
      return ErrorToast('Please fill in all information');

    if (timeFrom[0] >= timeTo[0]) {
      return ErrorToast('From time must be before to time');
    }

    const selectedWorkingHours = {
      day: selectedDayOfWeek[0],
      startTime: formatDateToTime24h(timeFrom[0]),
      endTime: formatDateToTime24h(timeTo[0]),
    };

    if (workingHourData?.multiple) {
      setWorkingHours((prev) => {
        if (workingHourData?.type === 'business') {
          return {
            ...prev,
            business: [
              ...prev.business,
              ...selectedDayOfWeek.map((day) => ({
                day,
                startTime: selectedWorkingHours.startTime,
                endTime: selectedWorkingHours.endTime,
              })),
            ],
          };
        } else {
          return {
            ...prev,
            delivery: [
              ...prev.delivery,
              ...selectedDayOfWeek.map((day) => ({
                day,
                startTime: selectedWorkingHours.startTime,
                endTime: selectedWorkingHours.endTime,
              })),
            ],
          };
        }
      });
    } else {
      if (workingHourData?.edit) {
        setWorkingHours((prev) => {
          if (workingHourData?.type === 'business') {
            return {
              ...prev,
              business: prev.business.map((hour, i) =>
                i === workingHourData.index ? selectedWorkingHours : hour
              ),
            };
          } else {
            return {
              ...prev,
              delivery: prev.delivery.map((hour, i) =>
                i === workingHourData.index ? selectedWorkingHours : hour
              ),
            };
          }
        });
      } else {
        setWorkingHours((prev) => {
          if (workingHourData?.type === 'business') {
            return {
              ...prev,
              business: [...prev.business, selectedWorkingHours],
            };
          } else {
            return {
              ...prev,
              delivery: [...prev.delivery, selectedWorkingHours],
            };
          }
        });
      }
    }

    closeModal?.();
    setSelectedDayOfWeek([]);
    setTimeFrom([dateTimeInitial]);
    setTimeTo([dateTimeInitial]);
  }

  const toggleDaySelection = (day: DayOfWeek) => {
    if (workingHourData?.multiple) {
      setSelectedDayOfWeek(
        (prevSelected) =>
          prevSelected?.includes(day)
            ? prevSelected?.filter((d) => d !== day) // Remove if already selected
            : [...(prevSelected || []), day] // Add if not selected
      );
    } else {
      setSelectedDayOfWeek([day]);
    }
  };

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`${workingHourData?.edit?.day ? 'Edit' : 'Add'} Day`}
      textAlign="left"
      width="400px"
    >
      <H5>Select Day</H5>
      <Separator />
      {daysOfWeekString.map(({ name, value }) => (
        <div key={value}>
          <AdminRadioInput
            label={name}
            checked={selectedDayOfWeek.includes(value)}
            onClick={() => toggleDaySelection(value)}
          />
          <Separator />
        </div>
      ))}
      <H5 $mt="32" $mb="16" $center>
        Select Hours From - To
      </H5>
      <Div $flex $gap="24px">
        <AdminDatePickerInputModal
          format="hh:mm a"
          options={{
            enableTime: true,
            dateFormat: 'Y-m-d H:i',
            noCalendar: true,
          }}
          placeholder="From"
          $textCenter
          onChange={setTimeFrom}
          value={timeFrom}
        />
        <AdminDatePickerInputModal
          format="hh:mm a"
          options={{
            enableTime: true,
            dateFormat: 'Y-m-d H:i',
            noCalendar: true,
          }}
          placeholder="To"
          $textCenter
          onChange={setTimeTo}
          value={timeTo}
        />
      </Div>

      <Button $selected $w="100%" $mt="32" onClick={() => handleAdd()}>
        Confirm
      </Button>
    </Modal>
  );
};
